import React, { useState, useMemo, useEffect } from 'react';
import Icon, { DownOutlined, CloseOutlined } from '@ant-design/icons';
import { Input, AutoComplete } from 'antd';
import { WidgetState, WidgetPortContext } from '@kemu-io/kemu-core/types';
// eslint-disable-next-line import/no-unresolved
import DataToBinaryFileProcessor, {
  DataToBinaryFileWidgetState,
  getDefaultState,
  OutPorts,
  MimeTypes,
} from '@kemu-io/kemu-core/widgets/dataToBinaryFile/index.js';
import { WidgetPort } from '@kemu-io/hs-types';
import classNames from 'classnames';
import { useDebouncedCallback } from 'use-debounce';
import GateIcon from '../../gateIcon/gateIcon';
import {
  GetPortsInformationFunction,
  GateUI,
  GateUIProps,
  GateCustomSettingsProps,
} from '../index.ts';
import { PortLocation } from '../../../types/canvas_t';
import useReactiveWidgetState from '../../../common/hooks/useReactiveWidgetState';
import useTranslation, { getTranslationFunction } from '../../../common/hooks/useTranslation';
import { SETTINGS_CONTAINER_CLASS } from '../../../common/constants';
import styles from './dataToBinaryFile.module.css';
import { ReactComponent as BinaryFileIcon } from '@src/assets/img/dataTypes/binaryFileIcon.svg';

const DataToBinaryFileWidget = (props: GateUIProps): React.JSX.Element => {
  const [state, setState] = useReactiveWidgetState<DataToBinaryFileWidgetState>(
    props.recipeId,
    props.thingRecipeId,
    props.info.id
  );

  const fixedState: DataToBinaryFileWidgetState = {
    ...getDefaultState(),
    ...state,
  };

  // Local state for input value to preserve cursor position
  const [localFileName, setLocalFileName] = useState(fixedState.fileName);

  // Sync local state when widget state changes externally
  useEffect(() => {
    setLocalFileName(fixedState.fileName);
  }, [fixedState.fileName]);

  const t = useTranslation('LogicMapper.Widgets.DataToBinaryFile.Body');

  // Debounced update to widget state
  const debouncedUpdateState = useDebouncedCallback((value: string) => {
    setState(current => ({
      ...current,
      fileName: value,
    }));
  }, 300);

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setLocalFileName(next); // Update local state immediately (preserves cursor)
    debouncedUpdateState(next); // Update widget state after user stops typing
  };

  return (
    <div className={styles.WidgetBody}>
      <Input
        className={styles.FileNameInput}
        size="small"
        value={localFileName}
        onChange={handleFileNameChange}
        placeholder={t('FileNamePlaceholder')}
      />
    </div>
  );
};

/** Icon to be added to the bar */
const WidgetBarIcon = (): React.JSX.Element => {
  return (
    <GateIcon icon={<Icon component={BinaryFileIcon} />} />
  );
};

const GateCustomSettings = (props: GateCustomSettingsProps): React.JSX.Element => {
  const { redrawPortsAndConnections } = props;
  const [state, setState] = useReactiveWidgetState<DataToBinaryFileWidgetState>(
    props.recipeId,
    props.blockId,
    props.gateInfo.id
  );

  const fixedState: DataToBinaryFileWidgetState = {
    ...getDefaultState(),
    ...state,
  };

  const t = useTranslation('LogicMapper.Widgets.DataToBinaryFile.Settings');
  const [searchValue, setSearchValue] = useState('');

  const handleMimeChange = (value: string | undefined) => {
    const nextValue = !value ? '' : value;
    setState(current => ({
      ...current,
      mimeType: nextValue,
    }), true);
    setSearchValue('');
    redrawPortsAndConnections();
  };

  const handleMimeSearch = (value: string) => {
    setSearchValue(value);
  };

  // Ensure currentMime is explicitly a string, never undefined
  const currentMime = fixedState.mimeType ?? '';

  // Create options from common mime types, filtering by search if active
  const mimeOptions = useMemo(() => {
    const commonMimeTypes = Object.values(MimeTypes) as string[];
    const options = commonMimeTypes.map(mime => ({
      value: mime,
      label: mime,
    }));

    // If user typed something not in the list, add it as an option
    if (searchValue && !commonMimeTypes.includes(searchValue)) {
      options.unshift({
        value: searchValue,
        label: searchValue,
      });
    }

    return options;
  }, [searchValue]);

  // Custom suffix icon: clear button when value exists, dropdown icon when empty
  const suffixIcon = currentMime ? (
    <CloseOutlined
      onClick={() => handleMimeChange('')}
      style={{ cursor: 'pointer' }}
    />
  ) : (
    <DownOutlined />
  );

  return (
    <div className={classNames(styles.SettingsContainer, SETTINGS_CONTAINER_CLASS)}>
      {props.children}

      <div className={classNames(styles.FieldRow)}>
        <label>{t('MimeLabel')}</label>
        <AutoComplete
          size="small"
          value={currentMime}
          onChange={handleMimeChange}
          onSearch={handleMimeSearch}
          placeholder={t('MimePlaceholder')}
          options={mimeOptions}
          filterOption={(inputValue, option) =>
            option?.value?.toLowerCase().includes(inputValue.toLowerCase()) ?? false
          }
          suffixIcon={suffixIcon}
        />
      </div>
    </div>
  );
};

const getPortsInformation: GetPortsInformationFunction = (
  state: WidgetState<DataToBinaryFileWidgetState>,
  widgetInfo,
  intl
) => {
  const portContext: WidgetPortContext = {
    recipePoolId: widgetInfo.recipePoolId,
    recipeType: widgetInfo.recipeType,
    thingRecipeId: widgetInfo.thingRecipeId,
    widgetId: widgetInfo.id,
  };

  const outputNames = DataToBinaryFileProcessor.getOutputNames(state, portContext);
  const inputNames = DataToBinaryFileProcessor.getInputNames(state, portContext);
  const t = getTranslationFunction(intl, 'LogicMapper.Widgets.DataToBinaryFile.Port');

  const inputPositions: Record<string, PortLocation> = {
    fileName: [0, 0.30, -1, 0],
    data: [0, 0.70, -1, 0],
  };

  const outputPositions: Record<string, PortLocation> = {
    [OutPorts.file]: [1, 0.5, 1, 0],
  };

  return {
    inputs: inputNames.map((input: WidgetPort) => ({
      name: input.name,
      type: input.type,
      position: inputPositions[input.name] || 'Left',
      label: t(input.name, input.name),
    })),
    outputs: outputNames.map((output: WidgetPort) => ({
      name: output.name,
      type: output.type,
      position: outputPositions[output.name] || 'Right',
      label: t(output.name, output.name),
    })),
  };
};

const widget: GateUI = {
  getPortsInformation,
  BarIcon: WidgetBarIcon,
  Element: DataToBinaryFileWidget,
  CustomSettingsDialog: GateCustomSettings,
  hasTitle: true,
  getWidgetTitle: (intl) => intl.formatMessage({
    id: 'LogicMapper.Widgets.DataToBinaryFile.Title',
    defaultMessage: 'Data to Binary File',
  }),
  getGatesBarTitle: (intl) => intl.formatMessage({
    id: 'LogicMapper.Widgets.DataToBinaryFile.Title',
    defaultMessage: 'Data to Binary File',
  }),
};

export default widget;


